import React, { useCallback, useEffect, useRef, useState } from "react";
import qz from "qz-tray";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useQzPrint } from "src/hooks/useQzPrint";
import { Tab } from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { AgGridReact } from "ag-grid-react";
import {
	defaultEqmInspectResultHeaderDto,
	EqmInspectResultDetailDto,
	EqmInspectResultHeaderDto,
} from "src/dtos/eqmInspectResult/eqmInspectResultDto";
import {
	EQM2080_R01DetailDto,
	EQM2080_R01DetailPivotDto,
	EQM2080_R01InspectMasterDto,
} from "./Dto_EQM2080_R01";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import {
	downloadExcel_EQM2080_R01,
	getEQM2080_R01AllCount,
	getEQM2080_R01DetailList,
	getEQM2080_R01InspectList,
	getEQM2080_R01List,
	getPdfBlob_EQM2080_R01,
	patchEQM2080_R01,
	postEQM2080_R01,
} from "./Api_EQM2080_R01";
import { EQM2080_R01Filter, useEQM2080_R01Store } from "./Store_EQM2080_R01";
import {
	flattenGridRowsToDetail,
	pivotDetailToGridRows,
} from "./Util_EQM2080_R01";
import { EQM2080_R01_EQUIPMENT_CD } from "./utils/tabConfig";

interface Props {
	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;
	niDetailGridRef: React.RefObject<AgGridReact<EQM2080_R01DetailPivotDto> | null>;
	cnDetailGridRef: React.RefObject<AgGridReact<EQM2080_R01DetailPivotDto> | null>;
}

export const Content_EQM2080_R01 = (props: Props) => {
	const { tabList, setMessage, niDetailGridRef, cnDetailGridRef } = props;
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	const {
		EQM2080_R01Filter,
		setEQM2080_R01PastFilter,
		EQM2080_R01List,
		setEQM2080_R01List,
		setEditEQM2080_R01,
		editEQM2080_R01,
		selectedEQM2080_R01,
		setSelectedEQM2080_R01,
		setEQM2080_R01NIDetailList,
		setOriginalEQM2080_R01NIDetailList,
		setEQM2080_R01CNDetailList,
		setOriginalEQM2080_R01CNDetailList,
		EQM2080_R01NIDetailList,
		EQM2080_R01CNDetailList,
		setNiDetailScrollTop,
		setCnDetailScrollTop,
		activeDetailTab,
		setAllCount,
		initData,
	} = useEQM2080_R01Store();

	useFetchActionbarAuth("EQM2080_R01");
	const [clickable, setClickable] = useState(true);
	const originalFlatDetailListRef = useRef<EQM2080_R01DetailDto[]>([]);
	const hasOriginalFlatDetailRef = useRef(false);
	const inspectMasterRef = useRef<EQM2080_R01InspectMasterDto[]>([]);
	const prevSearchKeyRef = useRef(
		`${EQM2080_R01Filter.inspectDt}|${EQM2080_R01Filter.equipmentCd}`,
	);

	const getParams = (filter: EQM2080_R01Filter) => ({
		compCd: filter.compCd || undefined,
		inspectDt: filter.inspectDt || undefined,
		equipmentCd: filter.equipmentCd || undefined,
	});

	const renewalAllCount = useCallback(async () => {
		const response = await getEQM2080_R01AllCount(getParams(EQM2080_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [EQM2080_R01Filter, setAllCount]);

	const applyDetailPivot = useCallback((
		flatList: EQM2080_R01DetailDto[],
		inspectMasterList: EQM2080_R01InspectMasterDto[] = inspectMasterRef.current,
	) => {
		const pivotRows = pivotDetailToGridRows(
			flatList,
			inspectMasterList,
			EQM2080_R01Filter.inspectDt,
		);

		setEQM2080_R01NIDetailList([]);
		setOriginalEQM2080_R01NIDetailList([]);
		setEQM2080_R01CNDetailList([]);
		setOriginalEQM2080_R01CNDetailList([]);
		if (activeDetailTab === EQM2080_R01_EQUIPMENT_CD.NICKEL_CHROME) {
			setEQM2080_R01NIDetailList(pivotRows);
			setOriginalEQM2080_R01NIDetailList(pivotRows);
			return;
		}
		setEQM2080_R01CNDetailList(pivotRows);
		setOriginalEQM2080_R01CNDetailList(pivotRows);
	}, [activeDetailTab, EQM2080_R01Filter.inspectDt, setEQM2080_R01NIDetailList, setOriginalEQM2080_R01NIDetailList, setEQM2080_R01CNDetailList, setOriginalEQM2080_R01CNDetailList]);

	const fetchInspectMaster = useCallback(async () => {
		const responseData = (await getEQM2080_R01InspectList(getParams(EQM2080_R01Filter))).data.data;
		if (responseData?.length > 0) {
			const inspectMasterList: EQM2080_R01InspectMasterDto[] = responseData.map((value) => ({
				eqmType: value.eqmType || "",
				eqmTypeNm: value.eqmTypeNm || "",
				inspectCd: value.inspectCd || "",
				inspectMethod: value.inspectMethod || "",
				inspectNm: value.inspectNm || "",
				remark: value.remark || "",
				standardValue: value.standardValue || "",
			}));
			inspectMasterRef.current = inspectMasterList;
			setMessage(
				`${EQM2080_R01Filter.equipmentNm || EQM2080_R01Filter.equipmentCd}의 점검코드 ${inspectMasterList.length}건을 조회하였습니다.`,
			);
			setTimeout(() => setMessage(""), 5000);
			return inspectMasterList;
		}

		inspectMasterRef.current = [];
		return [];
	}, [EQM2080_R01Filter, setMessage]);

	const fetchDetailList = useCallback(async (inspectNo: string) => {
		const responseData = (await getEQM2080_R01DetailList(inspectNo)).data.data ?? [];
		originalFlatDetailListRef.current = responseData;
		hasOriginalFlatDetailRef.current = true;
		applyDetailPivot(responseData);

		if (responseData.length > 0) {
			setMessage(
				`${selectedEQM2080_R01?.equipmentNm}의 점검결과 상세 ${responseData.length}건을 조회하였습니다.`,
			);
			setTimeout(() => setMessage(""), 5000);
		}
	}, [selectedEQM2080_R01, applyDetailPivot, setMessage]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setNiDetailScrollTop(null);
		setCnDetailScrollTop(null);

		const responseData = await getEQM2080_R01List(
			"search",
			getParams(EQM2080_R01Filter),
			EQM2080_R01List.length ?? 0,
		);
		const newList = [...responseData.data.data] as EqmInspectResultHeaderDto[];
		setEQM2080_R01List(newList);

		const processAllCount = await renewalAllCount();
		await fetchInspectMaster();

		if (newList.length > 0) {
			setSelectedEQM2080_R01(newList[0]);
			await fetchDetailList(newList[0].inspectNo);
			setEQM2080_R01PastFilter(EQM2080_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning("데이터가 없습니다.");
			setSelectedEQM2080_R01(undefined);
			setEQM2080_R01List([]);
			originalFlatDetailListRef.current = [];
			hasOriginalFlatDetailRef.current = false;
			if (inspectMasterRef.current.length > 0) {
				applyDetailPivot([], inspectMasterRef.current);
			} else {
				setEQM2080_R01NIDetailList([]);
				setOriginalEQM2080_R01NIDetailList([]);
				setEQM2080_R01CNDetailList([]);
				setOriginalEQM2080_R01CNDetailList([]);
				inspectMasterRef.current = [];
			}
		}
		setLoadingSpinner(false);
		return responseData;
	}, [EQM2080_R01Filter, EQM2080_R01List, fetchInspectMaster, fetchDetailList, setEQM2080_R01List, setEQM2080_R01PastFilter, setSelectedEQM2080_R01, setLoadingSpinner, setMessage, setNiDetailScrollTop, setCnDetailScrollTop, renewalAllCount, applyDetailPivot, setEQM2080_R01NIDetailList, setOriginalEQM2080_R01NIDetailList, setEQM2080_R01CNDetailList, setOriginalEQM2080_R01CNDetailList]);

	const initContainerData = useCallback(() => {
		initData();
		setEditEQM2080_R01({ ...defaultEqmInspectResultHeaderDto });
		originalFlatDetailListRef.current = [];
		hasOriginalFlatDetailRef.current = false;
		inspectMasterRef.current = [];
	}, [initData, setEditEQM2080_R01]);

	const handleInsertClick = useCallback(() => {
		showWarning("점검항목은 점검코드 목록 기준으로 표시됩니다.");
	}, []);

	const handleDeleteClick = useCallback(() => {
		showWarning("일자별 점검값은 셀에서 직접 삭제하세요.");
	}, []);

	const handleCreateClick = useCallback(() => {
		setSelectedEQM2080_R01(undefined);
		setEditEQM2080_R01({ ...defaultEqmInspectResultHeaderDto });
		originalFlatDetailListRef.current = [];
		hasOriginalFlatDetailRef.current = false;
		applyDetailPivot([], inspectMasterRef.current);
	}, [setSelectedEQM2080_R01, setEditEQM2080_R01, applyDetailPivot]);

	const validateInsertData = () => {
		if (!validateString(editEQM2080_R01?.judgeEmpno || "")) {
			showWarning("담당자를 입력하세요.");
			return false;
		}
		return true;
	};

	const handleSaveClick = useCallback(async () => {
		if (!validateInsertData()) return;

		const isNickelChrome = activeDetailTab === EQM2080_R01_EQUIPMENT_CD.NICKEL_CHROME;
		const activeGridRef = isNickelChrome ? niDetailGridRef : cnDetailGridRef;
		const activeDetailList = isNickelChrome
			? EQM2080_R01NIDetailList
			: EQM2080_R01CNDetailList;
		activeGridRef.current?.api?.stopEditing();

		setLoadingSpinner(true);

		try {
			const currentFlat = flattenGridRowsToDetail(
					activeDetailList,
					{
						inspectNo: editEQM2080_R01?.inspectNo || "",
						compCd: EQM2080_R01Filter.compCd || "",
						equipmentCd: EQM2080_R01Filter.equipmentCd || "",
						inspectCycle: editEQM2080_R01?.inspectCycle || "",
						judgeEmpno: editEQM2080_R01?.judgeEmpno || "",
						judgeEmpNm: editEQM2080_R01?.judgeEmpNm || "",
					},
					EQM2080_R01Filter.inspectDt,
				);

			const payloadBase = {
				inspectNo: editEQM2080_R01?.inspectNo || "",
				compCd: EQM2080_R01Filter.compCd || "",
				equipmentCd: EQM2080_R01Filter.equipmentCd || "",
				judgeEmpno: editEQM2080_R01?.judgeEmpno || "",
				judgeEmpNm: editEQM2080_R01?.judgeEmpNm || "",
			};

			const detailPayload: EqmInspectResultDetailDto[] = currentFlat.map((row) => ({
				...payloadBase,
				...row,
				inspectDt: formatDate(row.inspectDt),
			}));

			const response = validateString(editEQM2080_R01?.inspectNo || "")
				? await patchEQM2080_R01(detailPayload)
				: await postEQM2080_R01(detailPayload);

			if (!response?.data.successFlag) return;

			showSuccess("점검결과 저장이 완료되었습니다.");
			setMessage("점검결과 저장이 완료되었습니다.");
			setTimeout(() => setMessage(""), 5000);

			if ((response.data.data?.length ?? 0) > 0) {
				const savedHeader = (
					response.data.data[0] as { inspectResultHeaderDto?: EqmInspectResultHeaderDto }
				)?.inspectResultHeaderDto;
				if (savedHeader?.inspectNo) {
					setSelectedEQM2080_R01(savedHeader);
					if (EQM2080_R01List.length === 0) {
						setEQM2080_R01List([savedHeader]);
					}
				}
				await fetchDetailList(savedHeader?.inspectNo || editEQM2080_R01?.inspectNo || "");
			}
		} finally {
			setLoadingSpinner(false);
		}
	}, [activeDetailTab, EQM2080_R01NIDetailList, EQM2080_R01CNDetailList, EQM2080_R01Filter.compCd, EQM2080_R01Filter.equipmentCd, EQM2080_R01Filter.inspectDt, EQM2080_R01List, editEQM2080_R01, setLoadingSpinner, setMessage, setSelectedEQM2080_R01, setEQM2080_R01List, fetchDetailList, niDetailGridRef, cnDetailGridRef]);

	const handlePrintClick = useCallback(async () => {
		if (!EQM2080_R01Filter.equipmentCd || !EQM2080_R01Filter.inspectDt) {
			showWarning("사업장·설비·점검년월을 확인 후 출력하세요.");
			setMessage("사업장·설비·점검년월을 확인 후 출력하세요.");
			return;
		}
		if (!EQM2080_R01Filter.printDt) {
			showWarning("출력일자를 선택하세요.");
			setMessage("출력일자를 선택하세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_EQM2080_R01(EQM2080_R01Filter);
			if (blob.size === 0) {
				showWarning("해당 조건에 출력할 점검결과가 없습니다.");
				setMessage("해당 조건에 출력할 점검결과가 없습니다.");
				return;
			}
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [EQM2080_R01Filter, printPdfFromBlob, setLoadingSpinner, setMessage]);

	const handleExcelClick = useCallback(async () => {
		if (!selectedEQM2080_R01) {
			showWarning("선택된 점검결과가 없습니다.");
			return;
		}
		setLoadingSpinner(true);
		const response = await downloadExcel_EQM2080_R01(
			"",
			selectedEQM2080_R01.inspectNo,
			EQM2080_R01List.length ?? 0,
		);
		downloadSettingExcel({
			responseData: response.data.data,
			fileName: "설비점검결과 등록-excel.xlsx",
		});
		setLoadingSpinner(false);
	}, [selectedEQM2080_R01, EQM2080_R01List, setLoadingSpinner]);

	useEffect(() => {
		const searchKey = `${EQM2080_R01Filter.inspectDt}|${EQM2080_R01Filter.equipmentCd}`;
		if (prevSearchKeyRef.current === searchKey) return;
		prevSearchKeyRef.current = searchKey;
		if (!EQM2080_R01Filter.compCd || !EQM2080_R01Filter.equipmentCd) return;
		void fetchData();
	}, [EQM2080_R01Filter.inspectDt, EQM2080_R01Filter.equipmentCd, EQM2080_R01Filter.compCd, fetchData]);

	useEffect(() => {
		if (!selectedEQM2080_R01 || selectedEQM2080_R01.inspectNo) return;
		if (inspectMasterRef.current.length === 0) return;
		hasOriginalFlatDetailRef.current = false;
		originalFlatDetailListRef.current = [];
		applyDetailPivot([], inspectMasterRef.current);
	}, [selectedEQM2080_R01, applyDetailPivot]);

	return {
		clickable,
		setClickable,
		handlePrintClick,
		handleExcelClick,
		fetchData,
		initContainerData,
		handleInsertClick,
		handleDeleteClick,
		handleCreateClick,
		handleSaveClick,
	};
};
